import React, {useState} from 'react';
import {ChevronDownIcon, ChevronRightIcon} from '@heroicons/react/24/solid';
import {IStatisticModelGroup, IStatisticModelRunReq, IStatisticModelTransactionsReq} from '../../model/IStats';
import {TransactionType} from '../../model/TransactionType';
import {statisticModelApi} from '../../api/StatisticModelsService';
import TransactionReadOnlyTable from '../transactions/TransactionReadOnlyTable';
import Loader from '../ui/Loader';

interface StatsGroupsTableProps {
    groups: IStatisticModelGroup[];
    modelId: number;
    runBody: IStatisticModelRunReq;
    trType: TransactionType;
}

interface StatsTransactionsToggleProps {
    modelId: number;
    body: IStatisticModelTransactionsReq;
    trType: TransactionType;
    label?: string;
}

export const StatsTransactionsToggle: React.FunctionComponent<StatsTransactionsToggleProps> = ({
    modelId,
    body,
    trType,
    label = 'Show transactions',
}) => {
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [fetchTransactions, {data, isLoading}] = statisticModelApi.useFetchStatisticModelTransactionsMutation();

    const handleToggle = async () => {
        const nextOpen = !open;
        setOpen(nextOpen);
        if (nextOpen && !loaded) {
            await fetchTransactions({id: modelId, body});
            setLoaded(true);
        }
    };

    return (
        <div>
            <button
                type="button"
                onClick={handleToggle}
                className="inline-flex items-center text-sm font-medium text-indigo-700 hover:text-indigo-900"
            >
                {open ? <ChevronDownIcon className="h-4 w-4 mr-1"/> : <ChevronRightIcon className="h-4 w-4 mr-1"/>}
                {label}
            </button>
            {open && (
                <div className="mt-3">
                    {isLoading && <div className="relative min-h-[6rem]"><Loader/></div>}
                    {!isLoading && data && data.transactions.length === 0 && (
                        <p className="text-sm text-gray-500 px-4 py-2">No transactions in this group.</p>
                    )}
                    {!isLoading && data && data.transactions.length > 0 && (
                        <TransactionReadOnlyTable transactions={data.transactions} trType={trType}/>
                    )}
                </div>
            )}
        </div>
    );
};

const StatsGroupsTable: React.FunctionComponent<StatsGroupsTableProps> = ({groups, modelId, runBody, trType}) => {
    if (!groups.length) {
        return null;
    }

    const showCategory = groups.some((group) => group.category_name != null);
    const showMonth = groups.some((group) => group.month != null);
    const colSpan = 1 + (showCategory ? 1 : 0) + (showMonth ? 1 : 0) + 2;

    return (
        <div className="mt-6 overflow-hidden rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                <tr>
                    <th className="px-4 py-3 w-10"/>
                    {showCategory && (
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Category
                        </th>
                    )}
                    {showMonth && (
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Month
                        </th>
                    )}
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Count
                    </th>
                </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {groups.map((group, index) => (
                    <StatsGroupRows
                        key={`${group.category_id ?? 'none'}-${group.month ?? 'none'}-${index}`}
                        group={group}
                        showCategory={showCategory}
                        showMonth={showMonth}
                        colSpan={colSpan}
                        modelId={modelId}
                        runBody={runBody}
                        trType={trType}
                    />
                ))}
                </tbody>
            </table>
        </div>
    );
};

interface StatsGroupRowsProps {
    group: IStatisticModelGroup;
    showCategory: boolean;
    showMonth: boolean;
    colSpan: number;
    modelId: number;
    runBody: IStatisticModelRunReq;
    trType: TransactionType;
}

const StatsGroupRows: React.FunctionComponent<StatsGroupRowsProps> = ({
    group,
    showCategory,
    showMonth,
    colSpan,
    modelId,
    runBody,
    trType,
}) => {
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [fetchTransactions, {data, isLoading}] = statisticModelApi.useFetchStatisticModelTransactionsMutation();

    const handleToggle = async () => {
        const nextOpen = !open;
        setOpen(nextOpen);
        if (nextOpen && !loaded) {
            const body: IStatisticModelTransactionsReq = {
                ...runBody,
                filter_by_category: showCategory,
            };
            if (showCategory) {
                body.category_id = group.category_id;
            }
            if (group.month) {
                body.group_month = group.month;
            }
            await fetchTransactions({id: modelId, body});
            setLoaded(true);
        }
    };

    return (
        <>
            <tr>
                <td className="px-4 py-3">
                    <button
                        type="button"
                        onClick={handleToggle}
                        className="text-gray-500 hover:text-gray-800"
                        aria-label={open ? 'Hide transactions' : 'Show transactions'}
                    >
                        {open ? <ChevronDownIcon className="h-4 w-4"/> : <ChevronRightIcon className="h-4 w-4"/>}
                    </button>
                </td>
                {showCategory && (
                    <td className="px-4 py-3 text-sm text-gray-800">{group.category_name ?? '—'}</td>
                )}
                {showMonth && (
                    <td className="px-4 py-3 text-sm text-gray-800">{group.month ?? '—'}</td>
                )}
                <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                        {group.amount.map((balance) => (
                            <span
                                key={balance.currency.id}
                                className="px-2 inline-flex w-fit text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800"
                            >
                                {balance.currency.symbol + ' ' + balance.amount}
                            </span>
                        ))}
                    </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-800">{group.count}</td>
            </tr>
            {open && (
                <tr>
                    <td colSpan={colSpan} className="px-4 py-3 bg-gray-50">
                        {isLoading && <div className="relative min-h-[6rem]"><Loader/></div>}
                        {!isLoading && data && data.transactions.length === 0 && (
                            <p className="text-sm text-gray-500">No transactions in this group.</p>
                        )}
                        {!isLoading && data && data.transactions.length > 0 && (
                            <TransactionReadOnlyTable transactions={data.transactions} trType={trType}/>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
};

export default StatsGroupsTable;
